import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { ArrowRight, ShieldCheck, Wrench, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { landingStyles } from './Landing.styles';

const Landing: React.FC = () => {
    const { t } = useTranslation();

    const features = [
        {
            icon: ShieldCheck,
            title: t('landing.features.warranty.title'),
            desc: t('landing.features.warranty.desc'),
        },
        {
            icon: Wrench,
            title: t('landing.features.serviceHistory.title'),
            desc: t('landing.features.serviceHistory.desc'),
        },
        {
            icon: FileText,
            title: t('landing.features.documentation.title'),
            desc: t('landing.features.documentation.desc'),
        },
    ];

    return (
        <Box>
            {/* Hero */}
            <Box sx={landingStyles.hero}>
                <Box sx={landingStyles.heroOverlay} />
                <Container maxWidth="md">
                    <Box sx={landingStyles.heroBadge}>
                        <Typography variant="caption" color="text.secondary">
                            {t('landing.hero.badge')}{' '}
                            <Box component={Link} to="/about" sx={landingStyles.heroBadgeLink}>
                                {t('landing.hero.badgeLink')}
                            </Box>
                        </Typography>
                    </Box>
                    <Typography variant="h2" fontWeight={700} gutterBottom sx={landingStyles.heroTitle}>
                        {t('landing.hero.title')}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={landingStyles.heroSubtitle}>
                        {t('landing.hero.subtitle')}
                    </Typography>
                    <Box sx={landingStyles.heroActions}>
                        <Button component={Link} to="/register" variant="contained" size="large" sx={landingStyles.primaryCta}>
                            {t('landing.hero.registerCta')}
                        </Button>
                        <Button component={Link} to="/how-it-works" variant="text" color="inherit" size="large" endIcon={<ArrowRight size={20} />}>
                            {t('landing.hero.howItWorks')}
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Features */}
            <Box sx={landingStyles.featuresSection}>
                <Container maxWidth="lg">
                    <Box sx={landingStyles.featuresHeader}>
                        <Typography variant="overline" color="primary.main" fontWeight={600}>{t('landing.features.overline')}</Typography>
                        <Typography variant="h4" fontWeight={700} mt={1}>{t('landing.features.title')}</Typography>
                        <Typography color="text.secondary" mt={2}>
                            {t('landing.features.description')}
                        </Typography>
                    </Box>
                    <Box sx={landingStyles.featuresGrid}>
                        {features.map((feature) => {
                            const Icon = feature.icon;
                            return (
                                <Paper key={feature.title} elevation={0} sx={landingStyles.featureCard}>
                                    <Box sx={landingStyles.featureIcon}>
                                        <Icon size={24} color="white" />
                                    </Box>
                                    <Typography variant="h6" fontWeight={600} gutterBottom>{feature.title}</Typography>
                                    <Typography color="text.secondary">{feature.desc}</Typography>
                                </Paper>
                            );
                        })}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default Landing;
